import {Injectable} from "@angular/core";
import gql from "graphql-tag";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {StockItem} from "../../../shared/view-model/store-management/stock-item.type";
import {pruneEmpty, VisbosService} from "../../../core/visbos-core/graphql/visbos.service";
import {exportStockListEndpoint, stockFragments} from "../stock.service";
import {StockItemInput} from "../stock-item-input.type";
import {Observable} from "rxjs/Observable";
import {ApolloQueryResult, Subscription} from "apollo-client";
import {medicineFragments} from "../medicine.service";
import {productFragments} from "../product-services/product.service";
import {StockInput} from "../stock-input.type";
import {environment} from "../../../../environments/environment";
import {NgRedux} from "@angular-redux/store";
import {SessionActions} from "../../../shared/actions/session.actions";
import {IAppState} from "../../../shared/redux-store/store";
import {keywordFragments} from "../../../core/visbos-core/services/keyword.service";
import {FindStockItemsQuery, GetProductPackingsForStockItemQuery} from "../../../shared/graphql/graphql.types";
import {map} from "rxjs/operators";
import {accountFragments} from '../../../crm/services/accounting-unit.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';

export const exportStockItemListEndpoint = environment.exportUrl + 'stock-item-list/:adminUnitCuid';

export const stockItemFragments = {
  stockItem: gql`
    fragment StockItemFields on StockItem {
      cuid, label, name, short_name,
      properties { cuid, name, short_name, value, property { cuid } },
      is_editable,
      visbos_article_number,
      is_asset_packing,
      category {cuid, label, name},
      administration_unit { cuid, label }
      products { ... ProductFields },
      default_product { ... ProductFields },
      manufacturer { cuid label },
      supplier { cuid label },
      packings { ...ProductPackingFields },
      medicine { ...MedicineFields },
      stocks(adminUnitCuid: $adminUnitCuid) { ...StockDetailFields },
      keywords { ...KeywordFields }
      account {...AccountFields}
    }
    ${productFragments.product}
    ${productFragments.productPacking}
    ${stockFragments.stock}
    ${medicineFragments.medicine}
    ${keywordFragments.keyword}
    ${accountFragments.account}
  `,
  stockItemOverview: gql`
    fragment StockItemOverviewFields on StockItem {
      cuid label name short_name is_editable,
      visbos_article_number,
      category {cuid, label, name},
      administration_unit { cuid label },
      manufacturer { cuid label },
      supplier { cuid label },
      keywords { ...KeywordFields }
    }
    ${keywordFragments.keyword}
  `
};

export interface FetchStockItemsResult {
  fetchStockItems: StockItem[]
}

export const fetchStockItemsQuery = gql`
  query FetchStockItemsQuery($AU: String!, $with_parents: Boolean = true) {
    fetchStockItems(administration_unit_cuid: $AU, with_parents: $with_parents){
      ...StockItemOverviewFields
    }
  }

  ${stockItemFragments['stockItemOverview']}
`;

export const fetchStockItemsWithDetailsQuery = gql`
  query FetchStockItemsWithDetailsQuery($AU: String!, $with_parents: Boolean = true, $adminUnitCuid: String) {
    fetchStockItems(administration_unit_cuid: $AU, with_parents: $with_parents){
      ...StockItemFields
    }
  }

  ${stockItemFragments.stockItem}
`;

export const FindStockItems = gql`
  query FindStockItems($searchString:String!) {
    findStockItems(searchString:$searchString) {
      ...StockItemOverviewFields
    }
  }
  ${stockItemFragments.stockItemOverview}
`;

export const getProductPackingsForStockItem = gql`
  query GetProductPackingsForStockItem($stockItemCuid:String!) {
    productPackings(stockItemCuid: $stockItemCuid) {
      ...ProductPackingFields
    }
  }
  ${productFragments.productPacking}
`;

@Injectable()
export class StockItemService extends VisbosService {


  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions, private http: HttpClient) {
    super(apollo, ngRedux, sessionActions);
  }

  public getStockItem(cuid: string | Observable<string>, loadingVisible: boolean = false, adminUnitCuid?): ApolloQueryObservable<StockItem> {

    let data$ = this.fetchItems<StockItem>({
      query: gql`
        query FetchStockItemDetailQuery($cuid: String!, $adminUnitCuid: String) {
          fetchStockItemDetails(cuid: $cuid) {
            ...StockItemFields
          }
        }

        ${stockItemFragments['stockItem']}
      `,
      variables: {
        cuid: cuid,
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$
      }
    }, loadingVisible);

    return <ApolloQueryObservable<StockItem>>data$.pipe(map(({data}: any) => data.fetchStockItemDetails));
  }

  public prefetchStockItem(cuid: string, adminUnitCuid?): Subscription {

    return this.prefetchItems({
      query: gql`
        query PrefetchStockItemDetailQuery($cuid: String!, $adminUnitCuid: String) {
          fetchStockItemDetails(cuid: $cuid) {
            ...StockItemFields
          }
        }

        ${stockItemFragments['stockItem']}
      `,
      variables: {
        cuid: cuid,
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$
      }
    });

  }

  public prefetchStockItems(): Subscription {
    return this.prefetchItems({
      query: fetchStockItemsQuery,
      variables: {
        AU: this.ngRedux.getState().session.activeAdminUnit,
        with_parents: environment.view.show_parents,
        adminUnitCuid: this.activeAdminUnitCuid$
      }
    });
  }

  public fetchStockItems(variables: { adminUnitCuid?: string | Observable<string>, withParents?: boolean | Observable<boolean> } = {}, prefetch: boolean = false): ApolloQueryObservable<FetchStockItemsResult> {

    let options = {
      query: fetchStockItemsQuery,
      variables: {
        AU: variables.adminUnitCuid ? variables.adminUnitCuid : this.activeAdminUnitCuid$,
        with_parents: true, //variables.withParents ? variables.withParents : environment.view.show_parents
        adminUnitCuid: variables.adminUnitCuid ? variables.adminUnitCuid : this.activeAdminUnitCuid$
      }
    };

    if (prefetch) {
      this.prefetchItems(options);
      return null;
    } else {
      return this.fetchItems<FetchStockItemsResult>(options);
    }

  }

  public fetchStockItemsWithDetails(adminUnitCuid?: string | Observable<string>, withParents: boolean = true): ApolloQueryObservable<FetchStockItemsResult> {

    let data$ = this.fetchItems<FetchStockItemsResult>({
      query: fetchStockItemsWithDetailsQuery,
      variables: {
        AU: adminUnitCuid || this.activeAdminUnitCuid$,
        with_parents: withParents,
        adminUnitCuid: adminUnitCuid || this.activeAdminUnitCuid$
      }
    });

    return data$;
  }

  public findStockItems(searchString: string | Observable<string>): ApolloQueryObservable<FindStockItemsQuery> {
    return this.fetchItems({
      query: FindStockItems,
      variables: {
        searchString: searchString
      },
      fetchPolicy: "network-only"
    })
  }

  //TODO: Migrate Apollo
  public getProductPackingsForStockItem(stockItemCuid: string | Observable<string>): ApolloQueryObservable<GetProductPackingsForStockItemQuery> {

    return this.fetchItems({
      query: getProductPackingsForStockItem,
      variables: {
        stockItemCuid: stockItemCuid
      }
    });

  }

  //TODO: Refetch stocks as they are removed, too
  public deleteStockItem(cuid: string) {
    let data$ = this.mutate({
      mutation: gql`
        mutation DeleteStockItemMutation($cuid: String!) {
          deleteStockItem(cuid: $cuid)
        }`,
      variables: {
        cuid: cuid
      },
      updateQueries: this.deleteItemsFromQueries(cuid, 'fetchStockItems', 'FetchStockItemsQuery', 'FetchStockItemsWithParentsQuery'),
      refetchQueries: ['FetchStockItemsQuery', 'FetchStockItemsWithParentsQuery', 'FetchStocksQuery']
    })
    .subscribe(({data}: any) => {
      return data;
    });
    //TODO: REFACTOR
  }


  public createStockItem(stockItemData: StockItemInput, stocksData?: StockInput): Observable<StockItem> {

    return this.mutate({
      mutation: gql`
        mutation AddNewStockItemMutation($stock_item: CreateStockItemInput!,
          $stocks:[CreateStockInput], $adminUnitCuid: String) {
          addNewStockItem(stock_item:$stock_item, stocks: $stocks){
            ...StockItemFields
          }
        }

        ${stockItemFragments['stockItem']}
      `,
      variables: {
        stock_item: pruneEmpty(stockItemData),
        stocks: pruneEmpty(stocksData),
        adminUnitCuid: this.activeAdminUnitCuid
      },
      refetchQueries: ['FetchStockItemsQuery', 'FetchStockItemsWithParentsQuery', 'FetchManufacturersQuery']
    }).pipe(
      map(({data}: any) => {
        return data.addNewStockItem;
      }));

  }

  public updateStockItem(data: StockItemInput, cuid?: string, sync?: boolean): Observable<ApolloQueryResult<StockItem>> {

    data = pruneEmpty(data);

    return this.mutate({
      mutation: gql`
        mutation UpdateStockItemMutation($cuid: String, $stock_item: UpdateStockItemInput!, $sync: Boolean, $adminUnitCuid: String) {
          updateStockItem(cuid: $cuid, stock_item: $stock_item, sync: $sync) {
            ...StockItemFields
          }
        }

        ${stockItemFragments['stockItem']}
      `,
      variables: {
        cuid: cuid,
        stock_item: data,
        sync: sync,
        adminUnitCuid: this.activeAdminUnitCuid
      },
      refetchQueries: ['FetchManufacturersQuery']
    }).pipe(
      map((res: any) => {
        return res;
      }));
  }

  generateStockItemListExport(adminUnitCuid) {
    const headers = new HttpHeaders();
    headers.set('Accept', 'text/plain');
    return this.http.get(exportStockItemListEndpoint.replace(':adminUnitCuid', adminUnitCuid), {
      headers,
      responseType: 'arraybuffer',
      observe: 'response'
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/services/stock-item-service/stock-item.service.ts